import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField, Typography } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { shJson } from './api';

/**
 * Autocomplete multi-select de contratos (unidade ativa).
 * value: array de { id, numeroContrato, empresa, label }
 */
export default function ContratoMultiSelect({ value = [], onChange, disabled }) {
  const { unidadeSelecionada } = useAuth();
  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setFetchError('');
      try {
        const q = encodeURIComponent(input.trim());
        const data = await shJson(
          `/api/safety-huddle/contratos-opcoes?q=${q}&limit=40`,
          unidadeSelecionada
        );
        if (!cancelled) setOptions(data.items || []);
      } catch (e) {
        if (!cancelled) {
          setOptions([]);
          setFetchError(e.message || 'Erro ao buscar contratos');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 280);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [input, unidadeSelecionada]);

  const mergedOptions = useMemo(() => {
    const byKey = new Map();
    [...(value || []), ...options].forEach((o) => {
      const key = o.numeroContrato || o.label || String(o.id);
      if (!byKey.has(key)) byKey.set(key, o);
    });
    return [...byKey.values()];
  }, [value, options]);

  return (
    <>
      <Autocomplete
        multiple
        openOnFocus
        disabled={disabled}
        options={mergedOptions}
        value={value || []}
        loading={loading}
        filterOptions={(x) => x}
        onInputChange={(_, v, reason) => {
          if (reason === 'input' || reason === 'clear') setInput(v);
        }}
        onChange={(_, next) => onChange(next || [])}
        getOptionLabel={(o) => o.label || `${o.numeroContrato || ''} — ${o.empresa || ''}`}
        isOptionEqualToValue={(a, b) =>
          String(a.numeroContrato || a.id) === String(b.numeroContrato || b.id)
        }
        noOptionsText={
          loading ? 'Buscando…' : fetchError ? `Erro: ${fetchError}` : 'Nenhum contrato encontrado'
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Buscar contratos"
            placeholder="Digite o nome ou nº do contrato"
            error={!!fetchError}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {fetchError ? (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {fetchError} — reinicie o backend se a rota for recente.
        </Typography>
      ) : null}
    </>
  );
}

/** Serializa seleção para text_value (JSON). */
export function serializeContratosSelection(selected) {
  if (!selected?.length) return '';
  return JSON.stringify(
    selected.map((s) => ({
      numeroContrato: s.numeroContrato,
      empresa: s.empresa,
      label: s.label || `${s.numeroContrato} — ${s.empresa}`,
    }))
  );
}

export function parseContratosSelection(textValue) {
  if (!textValue) return [];
  try {
    const parsed = JSON.parse(textValue);
    if (Array.isArray(parsed)) {
      return parsed.map((s) =>
        typeof s === 'string'
          ? { label: s, empresa: s, numeroContrato: '' }
          : {
              numeroContrato: s.numeroContrato || '',
              empresa: s.empresa || '',
              label: s.label || `${s.numeroContrato || ''} — ${s.empresa || ''}`,
            }
      );
    }
  } catch {
    /* ignore */
  }
  return [];
}

export function isContratosSourceOptions(options) {
  return options && typeof options === 'object' && !Array.isArray(options) && options.source === 'contratos';
}

/** Flag explícita do setor (não usa mais o nome). */
export function sectorIntegraContratos(sector) {
  return !!(sector && sector.integraContratos);
}

/**
 * @deprecated Use sectorIntegraContratos(sector). Mantido só para não quebrar imports.
 */
export function isContratosSectorName() {
  return false;
}

export function shouldUseContratosMultiSelect(question, sector) {
  if (question?.responseType !== 'selecao') return false;
  // Somente origem explícita contratos (flag do setor não força list/manual)
  if (isContratosSourceOptions(question.options)) return true;
  // Legado: setor com flag + options vazias/ausentes
  if (sectorIntegraContratos(sector)) {
    const opts = question?.options;
    if (opts == null) return true;
    if (Array.isArray(opts) && opts.length === 0) return true;
    if (
      typeof opts === 'object' &&
      !Array.isArray(opts) &&
      !opts.source &&
      !(Array.isArray(opts.items) && opts.items.length)
    ) {
      return true;
    }
  }
  return false;
}
